/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/VirtualLineCancelAppointmentModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as CancelIcon } from "../../assets/icons/B2BEvents/CancelIcon.svg";
import styles from "./index.module.css";

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Record<string, unknown> | null} [props.appointment]
 * @param {() => Promise<{ error?: string }>} [props.onConfirmCancel]
 * @param {boolean} [props.isSubmitting]
 */
const VirtualLineCancelAppointmentModal = ({
  open,
  onClose,
  appointment,
  onConfirmCancel,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const handleClose = () => {
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      setFormError("");
      dialog?.showModal();
    } else {
      dialog?.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  const handleDismiss = () => {
    dialogRef.current?.close();
  };

  const handleConfirmCancel = async () => {
    if (!onConfirmCancel || isSubmitting) {
      return;
    }
    setFormError("");
    const result = await onConfirmCancel();
    if (result?.error) {
      setFormError(result.error);
      return;
    }
    dialogRef.current?.close();
  };

  const disableActions = isSubmitting;

  return (
    <>
      {open && <div className={styles.overlay} aria-hidden />}
      <dialog
        ref={dialogRef}
        className={styles.modal}
        data-appointment-id={
          appointment?._id != null ? String(appointment._id) : undefined
        }
      >
        <div className={styles.closeButtonContainer}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleDismiss}
            disabled={disableActions}
            aria-label={t("common.close")}
          >
            &times;
          </button>
        </div>

        <div className={styles.modalInner}>
          <h2 className={styles.modalTitle}>
            {t("pages.b2bEventUsersList.cancelModalTitle")}
          </h2>

          <div className={styles.textBlock}>
            <p className={styles.leadText}>
              {t("pages.b2bEventUsersList.cancelModalQuestion")}
            </p>
            <p className={styles.hintText}>
              {t("pages.b2bEventUsersList.cancelModalIrreversibleHint")}
            </p>
          </div>

          {formError ? (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          ) : null}
        </div>

        <div className={styles.modalButtons}>
          <button
            type="button"
            onClick={handleDismiss}
            className={styles.keepButton}
            disabled={disableActions}
          >
            {t("pages.b2bEventUsersList.cancelModalKeepAppointment")}
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={handleConfirmCancel}
            disabled={disableActions}
          >
            <CancelIcon className={styles.confirmButtonSvg} aria-hidden />
            <span>
              {isSubmitting
                ? t("pages.b2bEventUsersList.cancelModalSubmitting")
                : t("pages.b2bEventUsersList.cancelModalConfirmCancel")}
            </span>
          </button>
        </div>
      </dialog>
    </>
  );
};

export default VirtualLineCancelAppointmentModal;
